/**
 * RM's Member Highlights screen — what each row says and which control it
 * offers (Kapil, 2026-09-23). Pure, so it is unit-tested
 * (memberHighlightsAdmin.spec.js) rather than trusted inside the page.
 *
 * The flow a row walks, in order:
 *  1. A member shares a highlight with a restaurant; the relay email goes to
 *     Kapil first.
 *  2. The restaurant's own account has it at once — Messages, Fans page and a
 *     notification (2026-09-25). Kapil forwards the relay email and switches
 *     on "Passed along": his record that it went out.
 *  3. The restaurant says it would like to post it: "Ask member" emails the
 *     member (email first, always — "to ensure they are ok with it"), and
 *     they answer by replying (2026-09-24).
 *  4. When they reply yes, "Post publicly on {Restaurant}'s page" puts it on
 *     the restaurant's page — only the words the restaurant saw — and takes it
 *     down again when switched off.
 * A highlight the member made private shows so, with no controls: it must not
 * be forwarded.
 */

const firstName = row => String(row?.member?.name || '').split(' ')[0] || 'The member'

/** Whether the member's Network sees it too — and in the same words. */
export function networkLine(row) {
  const n = row?.network || {}
  if (!n.shared) return 'Not shared with their Network'

  return n.sameWords ? 'Also shared with their Network' : 'Shared with their Network in other words'
}

export const isPrivateNow = row => row?.status === 'withdrawn'

export const passedAlong = row => row?.delivery?.state === 'relayed'

/**
 * The public control for a row:
 *  - 'none'     made private, or not passed along yet (pass it along first)
 *  - 'ask'      "Ask member" — emails them
 *  - 'toggle'   asked: "Post publicly on {Restaurant}'s page", switched on
 *               once they reply yes (Kapil, 2026-09-24), with the ask's date
 *               beside it until then
 *  - 'declined' they said no (in RM AI)
 *
 * @returns {{ kind: string, label: string }}
 */
export function publicControl(row, { formatDate = d => String(d) } = {}) {
  if (!row || isPrivateNow(row)) return { kind: 'none', label: '' }
  const req = row.publicRequest
  const label = `Post publicly on ${row.restaurant?.name || 'the restaurant'}'s page`
  if (req?.state === 'approved') return { kind: 'toggle', label, note: '' }
  if (req?.state === 'declined') return { kind: 'declined', label: `${firstName(row)} said no` }
  if (req?.state === 'requested') {
    const when = req.requestedAt ? ` ${formatDate(req.requestedAt)}` : ''

    return { kind: 'toggle', label, note: `Asked${when} - waiting on ${firstName(row)}'s reply` }
  }
  if (!passedAlong(row)) return { kind: 'none', label: '' }

  return { kind: 'ask', label: 'Ask member' }
}

/** The one line a made-private row says instead of its controls. */
export const privateLine = row => (isPrivateNow(row)
  ? `${firstName(row)} made this private - don't pass it along`
  : '')
